import { useEffect, useState, useCallback } from 'react';
import { Card, Table, Button, Badge, Alert, Spinner, Form, Row, Col, Modal } from 'react-bootstrap';
import { api } from '../api/client';

const STATUSES = ['pending', 'paid', 'all'];

export default function AdminSettlements() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [payFor, setPayFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await api.get('/admin/settlements?status=' + status)); }
    catch (e) { setMsg({ type: 'danger', text: e.message }); }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const totalPending = rows.filter((r) => r.status === 'pending').reduce((s, r) => s + Number(r.net_payable), 0);

  return (
    <>
      <h3 className="mb-3">Settlements <small className="text-muted">(payouts to teachers)</small></h3>
      {msg.text && <Alert variant={msg.type} dismissible onClose={() => setMsg({ type: '', text: '' })}>{msg.text}</Alert>}

      <Card className="shadow-sm mb-3"><Card.Body>
        <Row><Col md={3}><Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </Form.Select></Col>
        <Col>
          {status === 'pending' && <div className="mt-1">Total pending to pay: <strong>₹{totalPending.toFixed(2)}</strong></div>}
        </Col>
        </Row>
      </Card.Body></Card>

      {loading ? <div className="text-center py-4"><Spinner /></div> : (
        <Table bordered hover responsive className="bg-white">
          <thead className="table-dark"><tr><th>#</th><th>Month</th><th>Teacher</th><th>Student</th><th>Gross</th><th>Commission</th><th>Net</th><th>Status</th><th>Paid at</th><th>Actions</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={10} className="text-center text-muted">No settlements</td></tr>}
            {rows.map((s) => (
              <tr key={s.id}>
                <td>{s.id}</td><td>{s.period_month}</td>
                <td>{s.teacher_name}</td><td>{s.student_name}</td>
                <td>₹{Number(s.gross).toFixed(0)}</td>
                <td>{Number(s.commission_pct).toFixed(1)}% (₹{Number(s.commission_amount).toFixed(0)})</td>
                <td><strong>₹{Number(s.net_payable).toFixed(0)}</strong></td>
                <td><Badge bg={s.status === 'paid' ? 'success' : 'warning'}>{s.status}</Badge></td>
                <td>{s.paid_at ? s.paid_at.slice(0, 16) : '-'}</td>
                <td>
                  {s.status === 'pending' && <Button size="sm" variant="success" onClick={() => setPayFor(s)}>Mark paid</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {payFor && <PayModal settlement={payFor} onClose={() => setPayFor(null)} onDone={() => { setPayFor(null); load(); }} />}
    </>
  );
}

function PayModal({ settlement, onClose, onDone }) {
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await api.post(`/admin/settlements/${settlement.id}/pay`, { notes });
      onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton><Modal.Title>Pay settlement #{settlement.id}</Modal.Title></Modal.Header>
      <Modal.Body>
        {err && <Alert variant="danger">{err}</Alert>}
        <p>
          Pay <strong>₹{Number(settlement.net_payable).toFixed(2)}</strong> to <strong>{settlement.teacher_name}</strong> for period <strong>{settlement.period_month}</strong>.
        </p>
        <Alert variant="info" className="small">
          This is a manual confirmation. Transfer the money to the teacher via your bank / UPI / etc., then add the reference below.
        </Alert>
        <Form.Group>
          <Form.Label>Payment reference / notes</Form.Label>
          <Form.Control value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g., UPI txn abc123" />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="success" onClick={submit} disabled={busy}>{busy ? '...' : 'Confirm paid'}</Button>
      </Modal.Footer>
    </Modal>
  );
}
